import * as React from 'react';

/**
 * Tabs accesibles mínimas (role=tablist/tab/tabpanel + aria-selected).
 * No hay un componente Tabs de shadcn/ui instalado en este proyecto — este
 * reemplaza esa pieza sin agregar dependencias nuevas. El estilo visual lo
 * define el consumidor vía `className` en cada trigger.
 */

interface TabsContextValue {
  value: string;
  setValue: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = React.useContext(TabsContext);
  if (!ctx) {
    throw new Error(`<${component}> debe usarse dentro de <Tabs>`);
  }
  return ctx;
}

interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
  children: React.ReactNode;
}

export function Tabs({ value, onValueChange, className, children }: TabsProps) {
  const ctx = React.useMemo<TabsContextValue>(
    () => ({ value, setValue: onValueChange }),
    [value, onValueChange],
  );
  return (
    <TabsContext.Provider value={ctx}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  className?: string;
  children: React.ReactNode;
}

export function TabsList({ className, children }: TabsListProps) {
  return (
    <div role="tablist" className={className}>
      {children}
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function TabsTrigger({ value, className, children }: TabsTriggerProps) {
  const { value: active, setValue } = useTabsContext('TabsTrigger');
  const selected = active === value;

  return (
    <button
      type="button"
      role="tab"
      id={`tab-${value}`}
      aria-selected={selected}
      aria-controls={`tabpanel-${value}`}
      tabIndex={selected ? 0 : -1}
      data-state={selected ? 'active' : 'inactive'}
      onClick={() => setValue(value)}
      className={className}
    >
      {children}
    </button>
  );
}

interface TabsPanelProps {
  value: string;
  className?: string;
  children: React.ReactNode;
}

export function TabsPanel({ value, className, children }: TabsPanelProps) {
  const { value: active } = useTabsContext('TabsPanel');
  if (active !== value) return null;

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${value}`}
      aria-labelledby={`tab-${value}`}
      className={className}
    >
      {children}
    </div>
  );
}
